import { useEffect, useState } from 'react';
import { X, Eye, EyeOff, Loader2, KeyRound, User as UserIcon, Mail, Lock } from 'lucide-react';
import { adminService } from '../_shared/api';
import type { User, UserRole } from '../_shared/types';
import { useModalKeys } from '../../../hooks/useModalKeys';

/**
 * UserFormModal — cria/edita vendedor ProspectUser (não SaasUser).
 *
 * SaasUser admin gerencia ProspectUsers (vendedores em campo) com seus
 * próprios tokens ERP. Cada vendedor pode ter credentials ERP separadas.
 *
 * Porte direto do apps/prospect-web.
 */
interface Props {
  editing: User | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormValues {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  erpToken: string;
  erpApp: string;
}

const EMPTY: FormValues = {
  name: '',
  email: '',
  password: '',
  role: 'user',
  erpToken: '',
  erpApp: '',
};

export default function UserFormModal({ editing, onClose, onSaved }: Props) {
  const [form, setForm] = useState<FormValues>(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({ ...EMPTY, name: editing.name, email: editing.email, role: editing.role });
      void adminService.getSecret(editing.id).then((s) => {
        if (s) setForm((f) => ({ ...f, erpApp: s.erpApp }));
      });
    } else {
      setForm(EMPTY);
    }
  }, [editing]);

  function set<K extends keyof FormValues>(key: K, value: FormValues[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      alert('Nome e e-mail são obrigatórios.');
      return;
    }
    if (!editing && !form.password.trim()) {
      alert('Senha é obrigatória para novo usuário.');
      return;
    }

    setSaving(true);
    try {
      let userId: string;
      if (editing) {
        await adminService.updateUser(editing.id, {
          name: form.name,
          email: form.email,
          role: form.role,
        });
        userId = editing.id;
      } else {
        const created = await adminService.createUser({
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
          erpToken: form.erpToken || undefined,
          erpApp: form.erpApp || undefined,
        });
        userId = created.id;
      }

      if (editing && form.erpToken.trim() && form.erpApp.trim()) {
        await adminService.upsertSecret(userId, {
          erpToken: form.erpToken,
          erpApp: form.erpApp,
        });
      }

      onSaved();
      onClose();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro: ' + (e.response?.data?.message ?? e.message ?? 'desconhecido'));
    } finally {
      setSaving(false);
    }
  }

  // Esc fecha; clique fora NÃO fecha (Fase 30). Enter submete o <form>.
  useModalKeys(onClose);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center sm:justify-center sm:p-4">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="bg-surface w-full sm:max-w-md sm:rounded-2xl border-t sm:border border-line flex flex-col max-h-[100vh] sm:max-h-[92vh]"
      >
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h2 className="font-bold text-fg text-lg">
            {editing ? 'Editar vendedor' : 'Novo vendedor'}
          </h2>
          <button type="button" onClick={onClose} className="text-fg-2 hover:text-fg">
            <X size={22} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <Field
            icon={<UserIcon size={16} />}
            label="Nome completo"
            value={form.name}
            onChange={(v) => set('name', v)}
            placeholder="João Silva"
          />

          <Field
            icon={<Mail size={16} />}
            label="E-mail"
            value={form.email}
            onChange={(v) => set('email', v)}
            placeholder="joao@email.com"
            type="email"
          />

          <div>
            <label className="text-[11px] uppercase tracking-wide text-fg-2">
              {editing ? 'Nova senha (deixe vazio para manter)' : 'Senha'}
            </label>
            <div className="mt-1 flex items-center bg-canvas border border-line rounded-xl px-3 h-11 focus-within:border-primary">
              <Lock size={16} className="text-fg-2" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                placeholder={editing ? 'Não alterar' : '••••••••'}
                className="flex-1 bg-transparent ml-2 text-fg text-sm outline-none placeholder:text-muted"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="p-1 text-muted hover:text-fg-2"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wide text-fg-2 mb-2 block">
              Perfil
            </label>
            <div className="flex gap-2">
              {(['user', 'supervisor', 'monitor', 'admin'] as UserRole[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => set('role', r)}
                  className={`flex-1 py-2.5 rounded-xl border text-xs font-medium transition ${
                    form.role === r
                      ? 'bg-primary-solid border-primary text-white'
                      : 'bg-canvas border-line text-fg-2 hover:bg-hover'
                  }`}
                >
                  {r === 'user'
                    ? 'Vendedor'
                    : r === 'supervisor'
                      ? 'Supervisor'
                      : r === 'monitor'
                        ? 'Monitor'
                        : 'Admin'}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-canvas/50 rounded-xl p-3 border border-line">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={14} className="text-primary" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-primary">
                Credenciais ERP
              </h3>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wide text-fg-2">
                ERP Token
              </label>
              <div className="mt-1 flex items-center bg-surface border border-line rounded-xl px-3 h-11 focus-within:border-primary">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={form.erpToken}
                  onChange={(e) => set('erpToken', e.target.value)}
                  placeholder="Token de acesso"
                  className="flex-1 bg-transparent text-fg text-sm outline-none placeholder:text-muted"
                />
                <button
                  type="button"
                  onClick={() => setShowToken((v) => !v)}
                  className="p-1 text-muted hover:text-fg-2"
                >
                  {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="mt-3">
              <label className="text-[11px] uppercase tracking-wide text-fg-2">
                ERP App
              </label>
              <input
                value={form.erpApp}
                onChange={(e) => set('erpApp', e.target.value)}
                placeholder="Identificador do app"
                className="mt-1 w-full bg-surface border border-line rounded-xl px-3 h-11 text-fg text-sm outline-none focus:border-primary placeholder:text-muted"
              />
            </div>

            {editing && (
              <p className="text-xs text-muted mt-2">
                Preencha apenas se quiser substituir as credenciais atuais.
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-line flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 h-11 bg-canvas hover:bg-hover rounded-xl text-fg-2 font-medium"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 h-11 bg-primary-solid hover:bg-primary-solid-hover disabled:bg-active rounded-xl text-white font-bold flex items-center justify-center gap-2"
          >
            {saving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <span>{editing ? 'Salvar' : 'Criar vendedor'}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  icon,
  label,
  value,
  onChange,
  placeholder,
  type,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wide text-fg-2">{label}</label>
      <div className="mt-1 flex items-center bg-canvas border border-line rounded-xl px-3 h-11 focus-within:border-primary">
        <span className="text-fg-2">{icon}</span>
        <input
          type={type ?? 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-transparent ml-2 text-fg text-sm outline-none placeholder:text-muted"
        />
      </div>
    </div>
  );
}
